import {Trans} from '@lingui/macro';
import {differenceBy, isNull, uniqBy} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandler, AsyncHandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {areArraysShallowEqual} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useNavigationBlocking} from '../../../../srcCommon/app/navigationBlocking';
import {FolderBreadcrumb} from '../../../components/folderBreadcrumbs/folderBreadcrumbs';
import {SettingsComposerContainer} from '../../../components/inboxes/composer/settings/settingsComposerContainer';
import {findInlineAttachmentsInHtml} from '../../../helpers/models/messageHelpers';
import {isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {UploadedAttachmentModel} from '../../../models/api/attachmentModel';
import {SignatureModel, SignatureTemplateModel} from '../../../models/api/signatureModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsSignaturesUrl} from '../../../models/url/settingsUrlModel';
import {
  ComposerVariableTypesEnum,
  InterfaceEventsEnum,
  SettingsTrackingTypesEnum,
  trackInterfaceEvent,
} from '../../../services/tracking';
import {isStaticVariable} from '../../../services/variableInterpolation/variableInterpolator';
import {markSignaturesAsStaleAction} from '../../../store/data/signatures/signaturesActions';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {SettingsBreadcrumbs} from '../../common/settingsBreadcrumbs';
import {SettingsDiscardChangesDialog} from '../../common/settingsDiscardChangesDialog';
import {SettingsFieldSection} from '../../common/settingsFieldSection';
import {SettingsFooter, StyledSettingsMain} from '../../common/settingsWrapper';
import {settingsHorizontalPaddingStyles} from '../../settingsCommonConstants';
import {SettingsHeader} from '../../settingsHeader';
import {SignaturesDeleteDialog} from '../dialog/signaturesDeleteDialog';
import {SignatureChannelDropdownContainer} from '../dropdown/signaturesChannelDropdownContainer';
import {
  renderSignatureChannelsDropdownAllIndividualChannelsLabel,
  renderSignatureChannelsDropdownAllTeamChannelsLabel,
} from '../dropdown/signaturesChannelDropdownHelpers';

/*
 * Constants.
 */

const defaultSignatureBody = `<div>—<br>{{user.name}}<br></div>`;

/*
 * Types.
 */

export interface SignatureFormInheritableProps {
  panelId: SettingsPanelId;
  signaturesUrl: SettingsSignaturesUrl;
}

interface SignatureFormBaseProps extends SignatureFormInheritableProps {
  signature?: SignatureModel;
  onSubmit: AsyncHandlerOf<SignatureTemplateModel>;
}

interface EditSignatureFormProps {
  isEditing: true;
  deleteSignature: AsyncHandler;
}

interface CreateSignatureFormProps {
  isEditing: false;
}

type SignatureFormProps = SignatureFormBaseProps & (EditSignatureFormProps | CreateSignatureFormProps);

interface ComputeCanSaveOptions {
  name: string;
  senderInfo: string | undefined;
  body: string;
  signature?: SignatureModel;
  isSaving: boolean;
  isEditing?: boolean;
  selectedChannels: ReadonlyArray<number>;
  isAllTeamChannelsSelected: boolean;
  isAllIndividualChannelsSelected: boolean;
  attachments: ReadonlyArray<UploadedAttachmentModel>;
  bodyIsEmpty: boolean;
}

/*
 * Style.
 */

export const StyledSettingsFormContainerDiv = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  position: relative;
`;

export const StyledSettingsContentWrap = styled.div`
  flex: 1;
  margin-bottom: 48px;
  ${settingsHorizontalPaddingStyles};
  display: flex;
  padding-right: 0;
`;

export const StyledSettingsContent = styled.div`
  flex: 1;
  min-height: min-content;
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  grid-row-gap: 24px;
`;

export const StyledSettingsField = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledInputWrap = styled.div`
  width: 440px;
`;

const StyledSubtitleDiv = styled.div`
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade90};
  margin-right: 8px;
`;

const StyledChannelPickerContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

/*
 * Component.
 */

export const SignatureForm: FC<SignatureFormProps> = (props) => {
  const theme = useUserTheme();
  const {isEditing, signaturesUrl, onSubmit, signature, panelId} = props;
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(signature?.name || '');
  const defaultSenderInfo = isEditing ? signature?.senderInfo : '{{user.name}}';
  const [senderInfo, setSenderInfo] = useState(defaultSenderInfo);
  const [selectedChannels, setSelectedChannels] = useState<ReadonlyArray<number>>(
    signature?.channelIds || [],
  );
  const isInTeamNamespace = isTeamNamespace(signaturesUrl.namespace);

  /**
   * The `isEnabledForAllChannels` value is saved to `signature.allChannels`.
   * The `isEnabledForAllPrivateChannels` value is saved to `signature.allPrivateChannels`.
   * See documentation in `RSignatureTemplateDirty` for {@see SignatureModel} for model property details
   * as the meanings of the fields change for a shared vs. private signature.
   */

  // isEnabledForAllChannels may be toggled for both shared and private signatures.
  const [isEnabledForAllChannels, setIsEnabledForAllChannels] = useState<boolean>(
    signature?.allChannels ?? true,
  );
  // isEnabledForAllPrivateChannels should only be toggled to true for shared signatures.
  const [isEnabledForAllPrivateChannels, setIsEnabledForAllPrivateChannels] = useState<boolean>(
    signature?.allPrivateChannels ?? false,
  );

  const [body, setBody] = useState(signature?.body || defaultSignatureBody);
  const [bodyIsEmpty, setBodyIsEmpty] = useState(false);
  const [attachments, setAttachments] = useState<ReadonlyArray<UploadedAttachmentModel>>(
    signature?.attachments || [],
  );

  const [shouldDisplayDeleteDialog, setShouldDisplayDeleteDialog] = useState(false);

  const canSave = useMemo(
    () =>
      computeCanSave({
        name,
        senderInfo,
        body,
        signature,
        isSaving,
        isEditing,
        selectedChannels,
        isAllIndividualChannelsSelected: isEnabledForAllPrivateChannels,
        isAllTeamChannelsSelected: isEnabledForAllChannels,
        attachments,
        bodyIsEmpty,
      }),
    [
      name,
      senderInfo,
      body,
      signature,
      isSaving,
      isEditing,
      selectedChannels,
      isEnabledForAllPrivateChannels,
      isEnabledForAllChannels,
      attachments,
      bodyIsEmpty,
    ],
  );

  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, canSave);
  const unblockAndNavigateToSignatures = () => {
    unblockNavigation();
    navigateToSignaturesOverview();
  };

  const addAttachment = (attachment: UploadedAttachmentModel) => {
    setAttachments((prevAttachments) => uniqBy([...prevAttachments, attachment], 'cid'));
  };

  const removeAttachment = (cid: string) => {
    setAttachments((prevAttachments) =>
      uniqBy(
        prevAttachments.filter((a) => a.cid !== cid),
        (a) => a.cid,
      ),
    );
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    const attachmentCidsInBody = findInlineAttachmentsInHtml(body).map((a) => a.cid);

    try {
      const newSignature = {
        name,
        senderInfo,
        selectedChannels,
        body,
        allChannels: isEnabledForAllChannels,
        // TODO: isAllTeamChannelsSelected shouldn't be toggleable in the UI for a personal signature, but this ensures we set it to false in that case.
        allPrivateChannels: isInTeamNamespace ? isEnabledForAllPrivateChannels : false,
        channelIds: selectedChannels,
        namespace: signaturesUrl.namespace,
        attachments: attachments.filter((a) =>
          // All attachments should be inline
          attachmentCidsInBody.includes(a.cid),
        ),
      };
      await onSubmit(newSignature);
      await dispatch(markSignaturesAsStaleAction());
    } catch {
      setIsSaving(false);
    }
  };

  const renderChannelDropdown = () => {
    const hasNoChannelsSelected = selectedChannels.length === 0;
    const inputItems = isInTeamNamespace
      ? [
          {
            label: renderSignatureChannelsDropdownAllIndividualChannelsLabel(),
            isSelected: isEnabledForAllPrivateChannels,
            onChange: () => {
              const isNotToggeable = hasNoChannelsSelected && !isEnabledForAllChannels;
              setSelectedChannels([]);
              setIsEnabledForAllPrivateChannels(!isEnabledForAllPrivateChannels || isNotToggeable);
            },
            key: 'signatures-channels-dropdown-item-team-namespace-individual-channels',
          },
          {
            label: renderSignatureChannelsDropdownAllTeamChannelsLabel(),
            isSelected: isEnabledForAllChannels,
            onChange: () => {
              const isNotToggeable = hasNoChannelsSelected && !isEnabledForAllPrivateChannels;
              setSelectedChannels([]);
              setIsEnabledForAllChannels(!isEnabledForAllChannels || isNotToggeable);
            },
            key: 'signatures-channels-dropdown-item-team-namespace-team-channels',
          },
        ]
      : [
          {
            label: renderSignatureChannelsDropdownAllIndividualChannelsLabel(),
            isSelected: isEnabledForAllChannels,
            onChange: () => {
              const isNotToggeable = hasNoChannelsSelected;
              setSelectedChannels([]);
              setIsEnabledForAllChannels(!isEnabledForAllChannels || isNotToggeable);
            },
            key: 'signatures-channels-dropdown-item-user-namespace-individual-channels',
          },
        ];

    return (
      <SignatureChannelDropdownContainer
        selectedItems={selectedChannels}
        onChange={(items) => {
          if (isNull(items) || items.length === 0) {
            setSelectedChannels([]);

            if (isInTeamNamespace) {
              setIsEnabledForAllChannels(true);
              setIsEnabledForAllPrivateChannels(false);
            } else {
              setIsEnabledForAllChannels(true);
            }
          } else {
            setIsEnabledForAllChannels(false);
            setIsEnabledForAllPrivateChannels(false);
            setSelectedChannels(items);
          }
        }}
        namespace={signaturesUrl.namespace}
        inputItems={inputItems}
      />
    );
  };

  const navigateToSignaturesOverview = () => {
    const newUrl = buildSettingsPath({
      ...signaturesUrl,
      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      signatureId: undefined,
    });
    dispatch(navigateToLinkAction(newUrl));
  };

  const renderTopLine = () => {
    if (signaturesUrl.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
      return null;
    }

    const breadcrumbs: ReadonlyArray<FolderBreadcrumb> = [];

    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="settings-signatures">Signatures</Trans>,
          onBreadcrumbClick: navigateToSignaturesOverview,
        }}
        breadcrumbs={breadcrumbs}
      />
    );
  };

  const renderBody = () => (
    <>
      <StyledSettingsField>
        <StyledInputWrap>
          <TextBox
            label={<Trans id="settings-signature-form-name">Name</Trans>}
            value={name}
            message={
              <Trans id="settings-signature-form-name-subtitle">
                Give your signature a name so you can quickly find it later.
              </Trans>
            }
            onChange={(newName) => setName(newName)}
          />
        </StyledInputWrap>
      </StyledSettingsField>
      <StyledSettingsField>
        <StyledInputWrap>
          <TextBox
            label={<Trans id="settings-signature-form-sender">Sender info</Trans>}
            value={senderInfo}
            message={
              <Trans id="settings-signatures-form-sender-subtitle">
                Your sender info will appear in the From line of the emails you send.
              </Trans>
            }
            onChange={(sender) => setSenderInfo(sender)}
          />
        </StyledInputWrap>
      </StyledSettingsField>
      <StyledSettingsField>
        <SettingsFieldSection
          title={<Trans id="settings-signature-form-channel-label">Channel access</Trans>}
        >
          <StyledChannelPickerContainerDiv>
            <StyledSubtitleDiv>
              <Trans id="settings-signatures-create-form-channel-subtitle">
                Make this signature visible for
              </Trans>
            </StyledSubtitleDiv>
            {renderChannelDropdown()}
          </StyledChannelPickerContainerDiv>
        </SettingsFieldSection>
      </StyledSettingsField>
      <StyledSettingsField>
        <SettingsComposerContainer
          initialCleanHtml={body}
          onChange={(text, isEmpty) => {
            setBody(text);
            setBodyIsEmpty(isEmpty);
          }}
          attachments={attachments}
          onAddAttachment={addAttachment}
          onRemoveAttachment={removeAttachment}
          onVariableInserted={(variableName) => {
            trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_SIGNATURE_VARIABLE_MENU_SELECT, {
              settings_type: isTeamNamespace(signaturesUrl.namespace)
                ? SettingsTrackingTypesEnum.TEAM
                : SettingsTrackingTypesEnum.PERSONAL,
              variable_name: variableName,
              variable_type: isStaticVariable(variableName)
                ? ComposerVariableTypesEnum.DEFAULT
                : ComposerVariableTypesEnum.CUSTOM,
              signature_id: signature?.id,
            });
          }}
          canEditRawHtml
          isAttachmentsButtonDisabled
        />
      </StyledSettingsField>
    </>
  );

  const renderFooter = () => {
    if (isEditing && !canSave) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button isRounded variant={ButtonVariantsEnum.SECONDARY} onClick={navigateToSignaturesOverview}>
          <Trans id="settings-form-cancel">Cancel</Trans>
        </Button>
        <Button isRounded variant={ButtonVariantsEnum.PRIMARY} onClick={handleSubmit} isDisabled={!canSave}>
          {isEditing ? (
            <Trans id="settings-form-save">Save</Trans>
          ) : (
            <Trans id="settings-form-create">Create</Trans>
          )}
        </Button>
      </SettingsFooter>
    );
  };

  const maybeRenderDeleteDialog = () => {
    if (!isEditing || !shouldDisplayDeleteDialog) {
      return null;
    }

    const onDeleteSignature = async () => {
      setShouldDisplayDeleteDialog(false);
      await props.deleteSignature();
      unblockAndNavigateToSignatures();
    };

    return (
      <SignaturesDeleteDialog
        signatures={signature ? [signature] : []}
        onDelete={onDeleteSignature}
        onClose={() => setShouldDisplayDeleteDialog(false)}
      />
    );
  };

  const maybeRenderDiscardChangesDialog = () => {
    if (!didBlockNavigation) {
      return null;
    }

    return <SettingsDiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />;
  };

  const maybeRenderRightActions = () => {
    if (!isEditing) {
      return null;
    }

    return (
      <Button
        isRounded
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        onClick={() => setShouldDisplayDeleteDialog(true)}
      >
        <Trans id="settings-signatures-edit-form-delete-action">Delete signature</Trans>
      </Button>
    );
  };

  return (
    <StyledSettingsFormContainerDiv>
      <SettingsHeader topLine={renderTopLine()} right={maybeRenderRightActions()}>
        {isEditing ? (
          <>
            <Icon name="featherSmall" color={theme.greys.shade70} size={22} />
            {signature?.name}
          </>
        ) : (
          <Trans id="settings-form-add-signature">Add signature</Trans>
        )}
      </SettingsHeader>
      <StyledSettingsMain>
        <StyledSettingsContentWrap>
          <StyledSettingsContent>{renderBody()}</StyledSettingsContent>
        </StyledSettingsContentWrap>
        {maybeRenderDeleteDialog()}
        {maybeRenderDiscardChangesDialog()}
      </StyledSettingsMain>
      {renderFooter()}
    </StyledSettingsFormContainerDiv>
  );
};

/*
 * Helpers.
 */

function computeCanSave(options: ComputeCanSaveOptions) {
  const {
    name,
    senderInfo,
    body,
    signature,
    isSaving,
    isEditing,
    selectedChannels,
    isAllTeamChannelsSelected,
    isAllIndividualChannelsSelected,
    attachments,
    bodyIsEmpty,
  } = options;

  if (isSaving) {
    return false;
  }

  if (!isEditing) {
    return !bodyIsEmpty;
  }

  return (
    !bodyIsEmpty &&
    (name !== signature?.name ||
      senderInfo !== signature?.senderInfo ||
      body !== signature.body ||
      !areArraysShallowEqual(selectedChannels, signature.channelIds) ||
      isAllTeamChannelsSelected !== signature.allChannels ||
      isAllIndividualChannelsSelected !== signature.allPrivateChannels ||
      attachments.length !== signature.attachments?.length ||
      differenceBy(attachments, signature.attachments, 'cid').length > 0)
  );
}
